import React, {useEffect, useMemo, useState} from 'react';
import {useAuth} from "../../../../../../utils/contexts/auth-context";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "@tanstack/react-query";
import {CustomAxiosError} from "../../../../../../logics/api_config";
import {MaterialReactTable, MRT_ActionMenuItem, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import {Delete, Edit} from "@mui/icons-material";
import {Avatar, Box, Grid, Popover} from "@mui/material";
import MuiReadOnlyBoxBody from "../../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import MRTMuiButton from "../../../../../ui/mui/mui_react_table/MRTMuiButton";
import AddIcon from "@mui/icons-material/Add";
import Loading from "../../../../../ui/Loading";
import MuiAlertDialog from "../../../../../ui/mui/mui_modal/MuiAlertDialog";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MuiFetchingSpinner from "../../../../../ui/mui/MuiFetchingSpinner";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import Papa from "papaparse";
import {CSVLink} from "react-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
    IMyCompanyBank
} from "../../../../../../utils/interfaces/my_company/my_company_registry/my_company_bank/IMyCompanyBank";
import useApiDeleteMyCompanyBank from "./api-hooks/useApiDeleteMyCompanyBank";
import useApiGetMyCompanyBanks from "./api-hooks/useApiGetMyCompanyBanks";
import useCustomCellRenderer from "./useCustomCellRenderer";
import MyCompanyBankForm from "./MyCompanyBankForm";
import MuiFabButtonBack from "../../../../../ui/mui/mui_buttons/fab/extended/MuiFabButtonBack";


interface IMyCompanyBankWithCustomField extends IMyCompanyBank {
    custom_field: JSX.Element;
}

interface Props {
    onSelect?: (obj: IMyCompanyBank | undefined) => void,
    onClose?: () => void;
    alreadySelectedObj?: IMyCompanyBank | undefined,
}


const MyCompanyBankTable = ({onSelect, onClose, alreadySelectedObj}: Props) => {
    const {selectedCompany} = useAuth();
    const theme = useTheme();
    const {t} = useTranslation()
    const queryClient = useQueryClient();

    const [selectedRow, setSelectedRow] = useState<IMyCompanyBank | undefined>()
    const [rows, setRows] = useState<IMyCompanyBank[] | undefined>()

    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteMyCompanyBank(selectedRow?.id);
    const {mutateAsync: apiDelete} = deleteMutation;

    const handleConfirmDelete = async () => {
        try {
            if (onSelect && (alreadySelectedObj?.id === selectedRow?.id)) {
                onSelect(undefined);
            }
            await apiDelete({pk: selectedRow?.id});
        } catch (error) {
        }
        setDeleteConfirmationOpen(false);
    };
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
    };


    const {
        data,
        isLoading,
        error,
        isFetching,
    } = useApiGetMyCompanyBanks();
    if (error) {
        const axiosError = error as CustomAxiosError;
        console.log(axiosError.response.data.detail);
    }


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const CustomCellRenderer: React.FC<{ row: IMyCompanyBank, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const [objsWithCustomField, setObjsWithCustomField] = useState<IMyCompanyBankWithCustomField[] | undefined>();
    useEffect(() => {
        const dataWithCustomField: IMyCompanyBankWithCustomField[] | undefined = rows?.map((row) => ({
            ...row,
            custom_field: <CustomCellRenderer row={row} theme={theme}/>
        }));
        setObjsWithCustomField(dataWithCustomField);
    }, [rows, theme]);


    const convertArrayOfObjectsToCSV = (data: IMyCompanyBank[]): string => {
        // Create new array of objects without the unnecessary fields
        const reducedData = data.map(({company, ...rest}) => rest);

        // Convert to CSV
        return Papa.unparse(reducedData, {
            delimiter: ';',
        });
    };


    const columns = useMemo<MRT_ColumnDef<IMyCompanyBank>[]>(
        () => [
            {
                accessorKey: 'custom_field',
                header: '',
                enableColumnActions: false,
                enableColumnFilter: false,
                enableColumnOrdering: false,
                enableSorting: false,
            },
            {
                accessorKey: 'iban',
                header: t('iban'),
                filterFn: 'contains',

            },
            {
                accessorKey: 'name',
                header: t('name'),
                filterFn: 'contains',
                accessorFn: (row) => (row.bank ? row.bank.name : ''),

            },
            {
                accessorKey: 'agency',
                header: t('agency'),
                filterFn: 'contains',
                accessorFn: (row) => (row.bank ? row.bank.agency : ''),

            },
            {
                accessorKey: 'abi',
                header: t('abi'),
                filterFn: 'contains',
                accessorFn: (row) => (row.bank ? row.bank.abi : ''),

            },
            {
                accessorKey: 'cab',
                header: t('cab'),
                filterFn: 'contains',
                accessorFn: (row) => (row.bank ? row.bank.cab : ''),

            },

        ],
        [rows]
    );


    const table = useMaterialReactTable<IMyCompanyBank>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        data: objsWithCustomField ? objsWithCustomField : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: true,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        muiTableBodyRowProps: getMuiTableBodyRowProps(theme),

        enableRowActions: true,
        renderRowActionMenuItems: ({closeMenu, row}) => [
            onSelect && onClose && (
                <MRT_ActionMenuItem
                    icon={<AdsClickIcon color="primary"/>}
                    key="select"
                    label={t("select")}
                    onClick={() => {
                        onSelect(row.original);
                        onClose();
                    }}
                    table={table}
                />),
            <MRT_ActionMenuItem
                icon={<Edit color="primary"/>}
                key="edit"
                label={t("edit")}
                onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                    setSelectedRow(row.original);
                    setIsEditFormOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
            <MRT_ActionMenuItem
                icon={<Delete color="warning"/>}
                key="delete"
                label={t("delete")}
                onClick={() => {
                    setSelectedRow(row.original);
                    setDeleteConfirmationOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
        ],
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <AccountBalanceIcon color={'primary'}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'banks'}/>
                    <MRTMuiButton
                        icon={<AddIcon style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true)
                        }} label={'add_bank'}/>
                    {rows &&
                        <CSVLink data={convertArrayOfObjectsToCSV(rows)} filename={'all_rows.csv'}>
                            {<FileDownloadIcon
                                style={{color: theme.palette.primary.main, fontSize: '2rem', marginTop: 8}}/>}
                        </CSVLink>
                    }

                </>
            </Box>
        ),
    });


    return <>
        {isLoading ? <Loading/> :
            <>
                {onClose &&
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <Box className={'mt-2 gap-x-2 mb-2'}>
                            <MuiFabButtonBack onClick={onClose}/>
                        </Box>
                    </Grid>}

                <MaterialReactTable table={table}/>

                <MuiAlertDialog
                    open={isDeleteConfirmationOpen}
                    title="delete_confirmation"
                    content="ask_for_delete"
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />

                <Popover
                    id={'edit_popover'}
                    open={isEditFormOpen}
                    anchorEl={anchorEl}
                    onClose={() => setIsEditFormOpen(false)}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                >
                    <MyCompanyBankForm objToEdit={selectedRow ? selectedRow : null}
                                       onClose={() => setIsEditFormOpen(false)}/>
                </Popover>

                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default MyCompanyBankTable;
